/**
 * ───────────────────────────────────────────────────────────────────────────
 * STICKY PURCHASE BAR REGISTRY
 * ───────────────────────────────────────────────────────────────────────────
 * The mobile lead-purchase bar is pinned to the bottom of the viewport. The
 * floating sales stack (Start Now + WhatsApp) is ALSO pinned bottom-right, so
 * without coordination the round pill sits on top of the pay button — the
 * single most expensive place on the site to have an overlap.
 *
 * Rather than guessing z-index values across files, the bar registers itself
 * here while it is on screen and the floating stack reads the registry:
 *   • it drops its "Start Now" pill (the bar already is the purchase CTA), and
 *   • it lifts WhatsApp above the bar's height.
 *
 * Same reference-counted pattern as src/lib/overlay.ts, so two bars (there
 * should never be two, but code changes) cannot unregister each other early.
 */
import { useEffect, useState } from 'react';

/** Height reserved for the bar, in px. Kept here so the floating stack and
 *  the page bottom-padding agree on one number. */
export const PURCHASE_BAR_HEIGHT = 84;

let count = 0;
const listeners = new Set<(n: number) => void>();

const emit = () => {
  listeners.forEach((fn) => {
    try {
      fn(count);
    } catch {
      /* a broken listener must never break a purchase CTA */
    }
  });
};

/** Imperative API. Returns a release function. */
export const acquirePurchaseBar = (): (() => void) => {
  count += 1;
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    count = Math.max(0, count - 1);
    emit();
  };
};

export const isPurchaseBarOpen = (): boolean => count > 0;

/** Register the bar while `active` is true. Auto-releases on unmount. */
export const usePurchaseBarLock = (active: boolean): void => {
  useEffect(() => {
    if (!active) return;
    return acquirePurchaseBar();
  }, [active]);
};

/** Subscribe to the registry. True while a sticky purchase bar is showing. */
export const usePurchaseBarOpen = (): boolean => {
  const [open, setOpen] = useState<boolean>(() => isPurchaseBarOpen());
  useEffect(() => {
    const fn = (n: number) => setOpen(n > 0);
    listeners.add(fn);
    fn(count);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return open;
};
